import React, { useEffect, useState } from 'react';
import { ERouteLinks } from 'models/route';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Grid, Typography } from '@mui/material';
import {
  getBreadcrumbs,
  getPrevLink,
  getSizeFullImage,
  getSizeImage,
  initialData,
} from './helpers';
import { isPathEqualToRouteName } from 'utils/route';
import { FileDropzoneContainer } from './FileDropzoneContainer';
import { PhotoRequirements } from './PhotoRequirements';
import { StyledUploadedFiles } from './style';
import { useGallery } from './hooks';
import { validateGalleryImage } from 'requests/gallery';
import { Loader } from 'components/atoms/Loader';
import {
  ESize,
  IEntryData,
  IGaleryEntryFormValues,
  IGaleryFormValues,
  IPhoto,
  IPhotoUpload,
} from 'models/galery';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGalleryEntrySchema } from './schema';
import { useGlobalProperty } from 'models/globalPropertyContext';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import SortableImages from './SortableImages';
import { ModalImages } from 'components/organisms/ModalImages';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { v4 as uuidv4 } from 'uuid';
import { EArtAncillaryTypeNameLowerCase } from 'constants/ArtAccessoryType';

const ManageImagesTemplate = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { clearAlert, samePageAlert } = useAlert();
  const { schema } = useGalleryEntrySchema();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const {
    id = null,
    frtId = null,
    type: artType = '',
  } = useParams<{ id: string; frtId: string; type: EArtAncillaryTypeNameLowerCase }>();
  const ownerId = id ?? frtId ?? '';
  const [searchParams] = useSearchParams();
  const { pathname }: any = useLocation();
  const typeLE = searchParams.get('type');
  const currentRoute = Object.values(ERouteLinks).find((route) =>
    matchPath({ path: route, end: true }, pathname),
  );
  const [photoFiles, setPhotoFiles] = useState<IPhotoUpload>({});
  const [currentPosition, setCurrentPosition] = useState<number>(1);
  const [openFullSize, setOpenFullSize] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [galleryThumbnails, setGalleryThumbnails] = useState<IPhoto>({});
  const [galleryFullImage, setGalleryFullImage] = useState<IPhoto>({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [entryWarnings, setEntryWarnings] = useState<Record<string, string[]>>({});
  const { getGalleryData, imagesData, handleSaveGalleryImage } = useGallery();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<IGaleryFormValues>({
    defaultValues: initialData,
    resolver: yupResolver(schema) as unknown as Resolver<IGaleryFormValues>,
  });
  const { fields, append, remove, move, replace } = useFieldArray({
    control,
    name: 'entryParameters',
  });

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const isEdit =
    isPathEqualToRouteName(pathname, ERouteLinks.LegalEntityEditImages) ||
    isPathEqualToRouteName(pathname, ERouteLinks.AncillariesEditImages) ||
    isPathEqualToRouteName(pathname, ERouteLinks.FirearmEditImages) ||
    isPathEqualToRouteName(pathname, ERouteLinks.FRTEditImages);
  const breadcrumbs = getBreadcrumbs(t, isEdit, ownerId, typeLE, artType, currentRoute);
  const prevLink = getPrevLink(ownerId, typeLE, artType, currentRoute);
  const sizeImage = getSizeImage(currentRoute);
  const sizeFullImage = getSizeFullImage(currentRoute);
  const withLinkedLicences =
    isPathEqualToRouteName(pathname, ERouteLinks.LegalEntityAddImages) ||
    isPathEqualToRouteName(pathname, ERouteLinks.LegalEntityEditImages);

  useEffect(() => {
    (async function getDataInit() {
      if (ownerId && !imagesData.length) {
        setIsLoading(true);
        await getGalleryData(ownerId, true, withLinkedLicences);
        setIsLoading(false);
      }
    })();
  }, [ownerId]);

  useEffect(() => {
    if (imagesData.length) {
      reset({
        entryIdsForRemoval: initialData.entryIdsForRemoval,
        entryParameters: imagesData.map((img) => ({
          uniqueId: img.uniqueId,
          filename: img.filename,
          position: img.position,
          createdDate: img.createdDate,
          licences: withLinkedLicences ? img.licences : [],
        })),
      });

      const imagesDataPhotos: IPhoto = {};
      const imagesDataFullPhotos: IPhoto = {};
      imagesData.forEach((data) => {
        if (data.presignedUrls) {
          imagesDataPhotos[data.uniqueId] = withLinkedLicences
            ? data.presignedUrls.THUMBNAIL
            : data.presignedUrls.THUMBNAIL_SQRT;

          imagesDataFullPhotos[data.uniqueId] = withLinkedLicences
            ? data.presignedUrls.FULL
            : data.presignedUrls.FULL_SQRT;
        }
      });

      setGalleryThumbnails(imagesDataPhotos);
      setGalleryFullImage(imagesDataFullPhotos);
    }
  }, [imagesData]);

  const onFileSelect = async (files: File[]) => {
    setUploadErrors([]);
    setIsValidating(true);

    const validated = await Promise.all(
      files.map(async (file) => {
        try {
          if (withLinkedLicences) {
            const result = await validateGalleryImage(file);
            return { file, warnings: result?.warnings ?? [], error: null as string | null };
          }
          return { file, warnings: [], error: null as string | null };
        } catch (e: any) {
          const errorMessage =
            e?.response?.data?.message || t('manageImages.validation.failed.text');
          return { file, warnings: [], error: errorMessage };
        }
      }),
    );

    setIsValidating(false);

    const errors = validated.map((it) => it.error).filter(Boolean) as string[];
    if (errors.length) {
      setUploadErrors(errors);
    }

    const accepted = validated.filter((it) => !it.error);
    if (!accepted.length) {
      return;
    }

    const current = getValues('entryParameters');

    const newEntries: IGaleryEntryFormValues[] = accepted.map(({ file }) => ({
      uniqueId: uuidv4(),
      filename: file.name,
      position: 0,
    }));

    const updated = [...newEntries, ...current].map((item, index) => ({
      ...item,
      position: index,
    }));

    replace(updated);

    const newPhotos: IPhotoUpload = {};
    const newWarnings: Record<string, string[]> = {};
    accepted.forEach(({ file, warnings }, index) => {
      newPhotos[newEntries[index].uniqueId] = file;
      if (warnings.length) {
        newWarnings[newEntries[index].uniqueId] = warnings;
      }
    });

    setPhotoFiles((prev) => ({
      ...newPhotos,
      ...prev,
    }));
    setEntryWarnings((prev) => ({
      ...newWarnings,
      ...prev,
    }));
  };

  const handleDelete = (entryId: string) => {
    const current = getValues('entryParameters');

    const removalIds = getValues('entryIdsForRemoval');
    if (imagesData && imagesData.find((d) => d.uniqueId === entryId)) {
      setValue('entryIdsForRemoval', [...removalIds, entryId]);
    }

    const updated = current
      .filter((item) => item.uniqueId !== entryId)
      .map((item, index) => ({
        ...item,
        position: index,
      }));

    replace(updated);

    setPhotoFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[entryId];
      return newFiles;
    });

    setEntryWarnings((prev) => {
      const newWarnings = { ...prev };
      delete newWarnings[entryId];
      return newWarnings;
    });
  };

  const handlePrev = () => {
    if (currentPosition > 1) {
      setCurrentPosition(currentPosition - 1);
    }
  };
  const handleNext = () => {
    if (currentPosition < fields.length) {
      setCurrentPosition(currentPosition + 1);
    }
  };

  const handleFullSize = (position: number) => {
    setCurrentPosition(position + 1);
    setOpenFullSize(true);
  };

  const onSubmitForm = async (formData: IGaleryFormValues) => {
    setIsSubmitLoading(true);

    const data: IGaleryFormValues = {
      entryIdsForRemoval: formData.entryIdsForRemoval,
      entryParameters: formData.entryParameters,
    };

    const res = await handleSaveGalleryImage(data, photoFiles);
    if (res) {
      navigate(prevLink);
    }

    setIsSubmitLoading(false);
  };

  return (
    <>
      <ModalImages
        open={openFullSize}
        onClose={() => setOpenFullSize(false)}
        onPrev={() => handlePrev()}
        onNext={() => handleNext()}
        currentPosition={currentPosition}
        setCurrentPosition={setCurrentPosition}
        imageThumbnails={{
          ...(galleryThumbnails ?? {}),
          ...(photoFiles ?? {}),
        }}
        galleryData={fields as IEntryData[]}
        imageFilesInitial={{
          ...(photoFiles ?? {}),
          ...(galleryFullImage ?? {}),
        }}
        sizeFullImage={sizeFullImage}
        downloadButton={false}
      />
      <Breadcrumbs items={breadcrumbs} />
      <StyledPageTitle variant="h4">
        {isEdit ? t('manageImages.editPhoto.label') : t('manageImages.addPhoto.label')}
      </StyledPageTitle>

      {samePageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}

      {isLoading ? (
        <Loader isLoading />
      ) : (
        <Grid sx={{ rowGap: '20px', mt: '20px' }}>
          <Grid container columnSpacing="20px" sx={{ rowGap: '20px', width: '100%' }}>
            <Grid
              size={{ xs: 12, lg: withLinkedLicences ? 6 : 12 }}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Typography sx={{ mb: 1, fontWeight: 'bold', fontSize: '14px' }}>
                {t('manageImages.photoFileUpload.label')}
              </Typography>
              <FileDropzoneContainer
                onFileSelect={onFileSelect}
                disabled={isSubmitLoading || isValidating}
                externalErrors={uploadErrors}
              />
            </Grid>
            {withLinkedLicences && (
              <Grid size={{ xs: 12, lg: 6 }}>
                <PhotoRequirements />
              </Grid>
            )}
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ mt: 3 }}>
            <form onSubmit={handleSubmit(onSubmitForm)}>
              {!!fields.length && (
                <>
                  <StyledUploadedFiles>
                    {t('manageImages.uploadedFiles.label')}
                  </StyledUploadedFiles>
                  <SortableImages
                    fields={fields}
                    getValues={getValues}
                    replace={replace}
                    move={move}
                    files={{
                      ...(galleryThumbnails ?? {}),
                      ...(photoFiles ?? {}),
                    }}
                    handleDelete={handleDelete}
                    longDateFormat={longDateFormat}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    handleFullSize={(position: number) => handleFullSize(position)}
                    withLinkedLicences={withLinkedLicences}
                    sizeImage={sizeImage}
                    sizeFullImage={sizeFullImage}
                    entryWarnings={entryWarnings}
                  />
                </>
              )}
              <Grid container columnSpacing="20px" sx={{ rowGap: 2, mt: '56px' }}>
                <Grid size={{ xs: 12, lg: 3 }}>
                  <Button
                    type="submit"
                    fullWidth
                    label={t('general.save.button')}
                    isLoading={isSubmitLoading}
                    id="save-button"
                    variant={EButtonVariants.contained}
                    disabled={
                      isSubmitLoading ||
                      (!fields.length && !getValues('entryIdsForRemoval').length)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 3 }}>
                  <Button
                    fullWidth
                    label={t('general.cancel.button')}
                    id="cancel-button"
                    disabled={isSubmitLoading}
                    variant={EButtonVariants.outlined}
                    onClick={() => navigate(prevLink)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export { ManageImagesTemplate };
